import { useRef, useEffect, useState } from 'react';
import { PublicClientApplication } from "@azure/msal-browser";
import ReactMarkdown from 'react-markdown';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';

const msalInstance = new PublicClientApplication({
  auth: {
    clientId: "a48c1d54-11b9-4ad7-a2b2-daa7b427ac82",
    authority: "https://login.microsoftonline.com/94255380-32ab-42e5-b722-359bb4a13c0f",
    redirectUri: window.location.origin
  }
});

const API_URL = "https://backend-copilot-gt-e0bpcbaaafbafze9.eastus2-01.azurewebsites.net";


const starterPrompts = [
  "Mam problem z połączeniem ze zdalnym pulpitem.",
  "Jak ustawić autoresponder?",
  "Jak zarezerwować salę konferencyjną?",  
  "Jaka jest procedura sygnalisty?",
  "Jakie są zasady korzystania z internetu w pracy?",
];

function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [user, setUser] = useState(null);
  const [showPrompts, setShowPrompts] = useState(true);

  const getFirstName = (fullName) => fullName.split(' ')[0];

  const submitMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
    setShowPrompts(false);

    try {
      const response = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(user?.token && { 'Authorization': `Bearer ${user.token}` }) },
        body: JSON.stringify({
          message: input,
          history: [...messages, userMessage]
        })
      });

      let data = {};
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
        const botMessage = { role: 'assistant', content: data.response, sources: data.sources || [] };
        setMessages(prev => [...prev, botMessage]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '⚠️ Serwer nie zwrócił poprawnej odpowiedzi.'
        }]);
      }
    } catch (error) {
      console.error('Błąd:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '❌ Wystąpił błąd przy pobieraniu odpowiedzi.'
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const chatBoxRef = useRef(null);
  useEffect(() => {
    chatBoxRef.current?.scrollTo({
      top: chatBoxRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [messages]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        await msalInstance.initialize();
        let account = msalInstance.getAllAccounts()[0];

        if (!account) {
          const loginResult = await msalInstance.loginPopup({
            scopes: ["api://a48c1d54-11b9-4ad7-a2b2-daa7b427ac82/api_copilotGT"]
          });
          account = loginResult.account;
        }

        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: ["api://a48c1d54-11b9-4ad7-a2b2-daa7b427ac82/api_copilotGT"],
          account
        });

        const token = tokenResponse.accessToken;

        const meRes = await fetch(`${API_URL}/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!meRes.ok) {
          throw new Error("Nie udało się pobrać danych użytkownika z backendu");
        }

        const me = await meRes.json();
        setUser({ name: me.name, email: me.email, token });

      } catch (err) {
        console.error("Błąd MSAL lub pobierania użytkownika:", err);
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    if (messages.length === 0 && user) {
      setMessages([{
        role: "assistant",
        content: `Cześć ${getFirstName(user.name)}! W czym mogę Ci pomóc?`
      }]);
    }
  }, [user, messages.length]);

  return (
    <>
      <div className="chat-container">
        <img
          src={`${process.env.PUBLIC_URL}/Logotypy.png`}
          alt="logo"
          className="background-watermark"
        />

        {user && (
          <div className="user-greeting">
            Witaj, {getFirstName(user.name)}
          </div>
        )}

        <div className="header-row">
          <h1 className="header">Navigator GT</h1>
          <button onClick={() => setMessages([])} className="reset-button">
            Nowy temat
          </button>
        </div>

        <AnimatePresence>
          {showPrompts && (
            <div className="starter-prompts">
              {starterPrompts.map((text, idx) => (
                <motion.div
                  key={text}
                  className="starter-prompt"
                  initial={{ opacity: 0, x: (idx % 2 === 0 ? -100 : 100), y: -20 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  exit={{ opacity: 0, x: (idx % 2 === 0 ? -200 : 200), y: 0 }}
                  transition={{ duration: 0.8, delay: idx * 0.15, type: "spring", stiffness: 100 }}
                  onClick={() => {
                    setInput(text);
                    setShowPrompts(false);
                  }}
                >
                  {text}
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>

        <div className="chat-box" ref={chatBoxRef}>
          {messages.map((msg, idx) => (
            <div key={idx} className={`message ${msg.role}`}>
              <ReactMarkdown>{msg.content}</ReactMarkdown>

              {msg.role === 'assistant' && msg.sources && msg.sources.length > 0 && (
                <div className="sources">
                  <hr style={{ marginTop: '0.5rem', marginBottom: '0.25rem' }} />
                  <small>
                    <strong>Źródła:</strong> {msg.sources.join(', ')}
                  </small>
                </div>
              )}
            </div>
          ))}

          {isTyping && (
            <div className="message assistant">
              <em>...</em>
            </div>
          )}
        </div>

        <div className="chat-form-container">
          <form
            className="chat-form"
            onSubmit={(e) => {
              e.preventDefault();
              submitMessage();
            }}
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submitMessage();
                }
              }}
              placeholder="Zadaj pytanie..."
              rows={2}
            />
            <button type="submit">Wyślij</button>
          </form>
        </div>
      </div>
    </>
  );
}

export default App;
